/**
 * Route Builder Context
 * Manages temporary route state for building custom shop routes
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import type { Shop } from "@shared/schema";
import { getRouteNavigationUrl, openGoogleMaps } from "@/lib/googleMaps";
import { useAuth } from "@/lib/authContext";

export type AddressSource = "home" | "custom";

export interface AddressSelection {
    source: AddressSource;
    address?: string;
    lat?: number;
    lng?: number;
    city?: string;
    postal_code?: string;
}

interface RouteBuilderContextType {
    shops: Shop[];
    isActive: boolean;
    startAddress: AddressSelection | null;
    endAddress: AddressSelection | null;
    extraStop: AddressSelection | null;
    hasAutoPopulatedHome: boolean;
    setHasAutoPopulatedHome: (val: boolean) => void;
    isCopiedRoute: boolean;
    setIsCopiedRoute: (val: boolean) => void;
    originalCopiedShopIds: string[];
    setOriginalCopiedShopIds: (val: string[]) => void;
    addShop: (shop: Shop) => void;
    removeShop: (shopId: string) => void;
    toggleShop: (shop: Shop) => void;
    setShops: (shops: Shop[]) => void;
    setStartAddress: (address: AddressSelection | null) => void;
    setEndAddress: (address: AddressSelection | null) => void;
    setExtraStop: (address: AddressSelection | null) => void;
    hasShop: (shopId: string) => boolean;
    clearRoute: () => void;
    openInGoogleMaps: () => void;
    toggleActive: () => void;
}

const RouteBuilderContext = createContext<RouteBuilderContextType | null>(null);

const STORAGE_KEY = 'vintmap-route-builder';

export function RouteBuilderProvider({ children }: { children: ReactNode }) {
    const [shops, setShops] = useState<Shop[]>(() => {
        // Load from sessionStorage on init
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    // Auto-clear shops older than 72 hours to prevent stale selections
                    const SEVENTY_TWO_HOURS = 72 * 60 * 60 * 1000;
                    if (data.savedAt && Date.now() - data.savedAt > SEVENTY_TWO_HOURS) {
                        sessionStorage.removeItem(STORAGE_KEY);
                        return [];
                    }
                    return data.shops || [];
                }
            } catch (e) {
                console.error('Failed to load route from sessionStorage', e);
            }
        }
        return [];
    });

    const [isActive, setIsActive] = useState(() => {
        // Load active state from sessionStorage
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    return data.isActive || false;
                }
            } catch (e) {
                console.error('Failed to load route state from sessionStorage', e);
            }
        }
        return false;
    });

    const [startAddress, setStartAddress] = useState<AddressSelection | null>(() => {
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    return data.startAddress || null;
                }
            } catch (e) {
                console.error('Failed to load startAddress from sessionStorage', e);
            }
        }
        return null;
    });

    const [endAddress, setEndAddress] = useState<AddressSelection | null>(() => {
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    return data.endAddress || null;
                }
            } catch (e) {
                console.error('Failed to load endAddress from sessionStorage', e);
            }
        }
        return null;
    });

    const [extraStop, setExtraStop] = useState<AddressSelection | null>(() => {
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    return data.extraStop || null;
                }
            } catch (e) {
                console.error('Failed to load extraStop from sessionStorage', e);
            }
        }
        return null;
    });

    const [hasAutoPopulatedHome, setHasAutoPopulatedHome] = useState(() => {
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    return data.hasAutoPopulatedHome || false;
                }
            } catch (e) {
                console.error('Failed to load hasAutoPopulatedHome from sessionStorage', e);
            }
        }
        return false;
    });

    const [isCopiedRoute, setIsCopiedRoute] = useState(() => {
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    return data.isCopiedRoute || false;
                }
            } catch (e) {
                console.error('Failed to load isCopiedRoute from sessionStorage', e);
            }
        }
        return false;
    });

    const [originalCopiedShopIds, setOriginalCopiedShopIds] = useState<string[]>(() => {
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    const data = JSON.parse(saved);
                    return data.originalCopiedShopIds || [];
                }
            } catch (e) {
                console.error('Failed to load originalCopiedShopIds from sessionStorage', e);
            }
        }
        return [];
    });

    const { user } = useAuth();
    const [isLoadedFromServer, setIsLoadedFromServer] = useState(false);

    // Load from server when user logs in
    useEffect(() => {
        if (!user || isLoadedFromServer) return;

        // Let's load the user's drafted route if available
        import("@/lib/queryClient").then(({ apiRequest }) => {
            apiRequest("GET", "/api/users/me/draft-route")
                .then(async (res) => {
                    const data = await res.json();
                    if (data?.draft_route) {
                        const { shops: s, isActive: a, startAddress: sa, endAddress: ea, extraStop: es } = data.draft_route;
                        if (s?.length > 0) {
                            setShops(s);
                            setIsActive(a || false);
                            if (sa) setStartAddress(sa);
                            if (ea) setEndAddress(ea);
                            if (es) setExtraStop(es);
                            if (data.draft_route.hasAutoPopulatedHome !== undefined) {
                                setHasAutoPopulatedHome(data.draft_route.hasAutoPopulatedHome);
                            }
                        }
                    }
                    setIsLoadedFromServer(true);
                })
                .catch(console.error);
        });
    }, [user, isLoadedFromServer]);

    // Save to sessionStorage and Server whenever shops, addresses or active state changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                const routeData = { shops, isActive, startAddress, endAddress, extraStop, hasAutoPopulatedHome, isCopiedRoute, originalCopiedShopIds, savedAt: Date.now() };
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(routeData));

                // Only save to server if it has actually been loaded to avoid overwriting remote with empty local state blindly
                if (user && isLoadedFromServer && shops.length > 0) {
                    import("@/lib/queryClient").then(({ apiRequest }) => {
                        apiRequest("PUT", "/api/users/me/draft-route", routeData).catch(console.error);
                    });
                } else if (user && isLoadedFromServer && shops.length === 0) {
                    import("@/lib/queryClient").then(({ apiRequest }) => {
                        apiRequest("PUT", "/api/users/me/draft-route", null).catch(console.error);
                    });
                }
            } catch (e) {
                console.error('Failed to save route to sessionStorage', e);
            }
        }
    }, [shops, isActive, startAddress, endAddress, extraStop, hasAutoPopulatedHome, isCopiedRoute, originalCopiedShopIds, user, isLoadedFromServer]);

    const addShop = useCallback((shop: Shop) => {
        setShops(prev => {
            // Check if shop already exists
            if (prev.some(s => s.id === shop.id)) {
                return prev;
            }
            return [...prev, shop];
        });
    }, []);

    const removeShop = useCallback((shopId: string) => {
        setShops(prev => prev.filter(s => s.id !== shopId));
    }, []);

    const toggleShop = useCallback((shop: Shop) => {
        setShops(prev => {
            if (prev.some(s => s.id === shop.id)) {
                return prev.filter(s => s.id !== shop.id);
            }
            return [...prev, shop];
        });
    }, []);

    const hasShop = useCallback((shopId: string) => {
        return shops.some(s => s.id === shopId);
    }, [shops]);

    const clearRoute = useCallback(() => {
        setShops([]);
        setIsActive(false);
        setStartAddress(null);
        setEndAddress(null);
        setExtraStop(null);
        setHasAutoPopulatedHome(false);
        setIsCopiedRoute(false);
        setOriginalCopiedShopIds([]);
        // DO NOT reset isLoadedFromServer to false, or the effect will re-fetch the old 
        // server state if it wasn't deleted fast enough.
        // setIsLoadedFromServer(false);

        // Also wipe sessionStorage immediately
        if (typeof window !== 'undefined') {
            try {
                sessionStorage.removeItem(STORAGE_KEY);
            } catch (e) { /* ignore */ }
        }

        // Explicitly clear from server immediately so it's gone
        import("@/lib/queryClient").then(({ apiRequest }) => {
            apiRequest("PUT", "/api/users/me/draft-route", null).catch(console.error);
        });
    }, []);

    const toggleActive = useCallback(() => {
        setIsActive((prev: boolean) => !prev);
    }, []);

    const openInGoogleMaps = useCallback(() => {
        if (shops.length === 0) return;

        const url = getRouteNavigationUrl(shops);
        if (url) {
            openGoogleMaps(url);
        }
    }, [shops]);

    return (
        <RouteBuilderContext.Provider
            value={{
                shops,
                isActive,
                startAddress,
                endAddress,
                extraStop,
                hasAutoPopulatedHome,
                setHasAutoPopulatedHome,
                isCopiedRoute,
                setIsCopiedRoute,
                originalCopiedShopIds,
                setOriginalCopiedShopIds,
                addShop,
                removeShop,
                toggleShop,
                setShops,
                setStartAddress,
                setEndAddress,
                setExtraStop,
                hasShop,
                clearRoute,
                openInGoogleMaps,
                toggleActive,
            }}
        >
            {children}
        </RouteBuilderContext.Provider>
    );
}

export function useRouteBuilder() {
    const context = useContext(RouteBuilderContext);
    if (!context) {
        throw new Error('useRouteBuilder must be used within RouteBuilderProvider');
    }
    return context;
}
